import {useEmailValidator} from '@app/hooks/useValidators';
import {FormControlField} from '../FormField';
import type {FieldProps} from '../FormField';
import {useTranslation} from '@app/hooks/useTranslation';
import {Button, FormHelperText, Stack, Text} from '@tempo/core';
import {useField} from 'formik';
import {setLoginPrefill} from '@app/modules/autofill';
import {TextWithNavLink} from '@app/components/TextWithLink';
import {WarningIcon} from '@tempo/icons';
import {useAppRedirectionConfig} from '@app/modules/redirection/hooks';
import {useRouter} from 'next/router';
import {withQuery} from '@app/utils/utils';
import {getTrackAcqPageName} from '@app/utils/offers';
import {logClick} from '@app/modules/tracking/gtm';
import type {AuthSuccessCallback} from '@app/components/AuthForms/context';

export const EmailField: React.FC<
	FieldProps<string> & {
		suggest: boolean;
		checkEmailUnique: boolean;
		isAcquisition?: boolean;
		cb?: AuthSuccessCallback;
		fontSize?: string;
	}
> = ({checkEmailUnique, suggest, isAcquisition = false, cb, ...props}) => {
	const [emailValidator, {suggestion, removeSuggestion}] =
		useEmailValidator(checkEmailUnique);
	const [, , {setValue}] = useField(props.name);
	const t = useTranslation();

	return (
		<FormControlField
			type="email"
			validate={emailValidator}
			label={t('common.label_email')}
			customErrorRenderer={() => {
				if (suggest && suggestion?.type === 'switch_to_login') {
					return isAcquisition ? (
						<SwitchToLoginSuggestionOffersAcq cb={cb} />
					) : (
						<SwitchToLoginSuggestion />
					);
				}
			}}
			hint={
				suggest && suggestion?.type === 'suggestion' ? (
					<EmailSuggestion
						suggestion={suggestion.suggestion}
						handleEmailSuggest={() => {
							setValue(suggestion.suggestion);
							removeSuggestion();
						}}
					/>
				) : undefined
			}
			validateOnChange={true}
			{...props}
		/>
	);
};

const Suggestion: React.FC<React.PropsWithChildren> = ({children}) => {
	return (
		<FormHelperText mb="spacing.s">
			<Stack direction="row" spacing="spacing.s" alignItems="center">
				<WarningIcon color="icon.neutral.secondary.default" />
				{children}
			</Stack>
		</FormHelperText>
	);
};

const SwitchToLoginSuggestion: React.FC = () => {
	const t = useTranslation();
	const config = useAppRedirectionConfig();
	const {asPath} = useRouter();
	const sp = new URLSearchParams(asPath.split('?')[1] || '');
	const [{value}] = useField('email');
	const handlePrefill = () => {
		if (value) {
			setLoginPrefill({email: value.trim()});
		}
	};
	return (
		<Suggestion>
			<TextWithNavLink
				onClick={handlePrefill}
				text={t('error.accountalrdyassociatedwithemail')}
				href={withQuery(config.getAuthPaths().login, sp)}
				data-testid="switch-to-login-suggestion"
				textAlign="start"
			>
				{t('formal.title_login')}
			</TextWithNavLink>
		</Suggestion>
	);
};

const SwitchToLoginSuggestionOffersAcq: React.FC<{
	cb?: AuthSuccessCallback;
}> = ({cb}) => {
	const t = useTranslation();
	const [{value}] = useField('email');
	const {asPath} = useRouter();
	const pathname = asPath.split('?')[0];

	const trackSwitch = () => {
		const pageName = getTrackAcqPageName(pathname);
		logClick({
			eventcategory: pageName,
			eventaction: 'access-pay-login',
			eventlabel: 'login_error_field',
		});
	};

	const handleClick = () => {
		trackSwitch();
		if (value) {
			setLoginPrefill({email: value.trim()});
		}
	};

	return (
		<FormHelperText mb="spacing.s">
			<Text color="text.accent.negative.default" lineHeight="shorter">
				{t('error.accountalrdyassociatedwithemail')}{' '}
				<Button
					as="span"
					data-testid="switch-to-login-suggestion"
					color="background.accent.primary.default"
					_active={{color: 'background.accent.primary.pressed'}}
					fontWeight="bold"
					variant="link"
					textTransform="none"
					height="auto"
					minHeight="0"
					minWidth="unset"
					p={0}
					verticalAlign="baseline"
					onClick={() => {
						handleClick();
						cb?.('0' as any);
					}}
				>
					{t('formal.title_login')}
				</Button>
			</Text>
		</FormHelperText>
	);
};

export const EmailSuggestion: React.FC<{
	handleEmailSuggest: () => void;
	suggestion: string;
}> = ({handleEmailSuggest, suggestion}) => {
	const t = useTranslation();
	return (
		<Suggestion>
			<Text
				data-testid="email-suggestion-message"
				onClick={handleEmailSuggest}
				dangerouslySetInnerHTML={{
					__html: t('formal.baddomainnamesuggestion', {
						sprintf: [
							`<u style="color: var(--tempo-colors-text-accent-primary-default); cursor: pointer">${suggestion}</u>`,
						],
					}),
				}}
			/>
		</Suggestion>
	);
};
